import { RightOutlined } from "@ant-design/icons";
import {
  getFilterDisplayText,
  isDateFilter,
  isNumericFilter,
} from "./filtersUtil";
import { isGeoFilter } from "./geoFilterUtil";
import { getIconForDataType } from "./FilterIcons";
import { toTitleCase } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import type { FilterConditionValue, FilterType } from "./filterTypes";
import { Tooltip } from "antd";

type FilterProps = {
  filter: string;
  dataType?: FilterType;
  isSelected?: boolean;
  hasFilterValues?: boolean;
  filterValue?: FilterConditionValue;
  onClick?: () => void;
  showGlowEffect?: boolean;
  columnsTitleCreator?: (column: string) => string;
  disabled?: boolean;
  showAsAvailable?: boolean;
};

const FilterTile = ({
  filter,
  dataType,
  isSelected = false,
  hasFilterValues = false,
  filterValue,
  onClick,
  showGlowEffect = false,
  columnsTitleCreator,
  disabled = false,
  showAsAvailable = false,
}: FilterProps) => {
  const getBorderColor = () => {
    if (disabled) return "#e5e5e5";
    if ((isSelected || showAsAvailable) && hasFilterValues) return "#0C98D0";
    if (isSelected) return "#D2EFF9";
    if (hasFilterValues) return "rgba(0, 0, 0, 0.12)";
    return "#0000001f";
  };

  const getBackgroundColor = () => {
    if (disabled) return "#f5f5f5";
    if (isSelected && hasFilterValues) return "#E6F7FF";
    if (hasFilterValues) return "#E6F7FF";
    return "";
  };

  const getBoxShadow = () => {
    if (
      (showGlowEffect && isSelected) ||
      (showAsAvailable && hasFilterValues)
    ) {
      return "-1px -1px 6px 0 rgba(100, 189, 225, 0.30), 1px 1px 6px 0 rgba(100, 189, 225, 0.30)";
    }
    return "";
  };

  const displayText =
    filterValue !== undefined
      ? getFilterDisplayText(filterValue, dataType!)
      : "";

  // Check if this is a date range or numeric range filter
  const isDateRange =
    dataType === "datetime" &&
    filterValue !== undefined &&
    isDateFilter(filterValue) &&
    filterValue.length > 0 &&
    filterValue[0].condition === "between";

  const isNumericRange =
    dataType === "numeric" &&
    filterValue !== undefined &&
    isNumericFilter(filterValue) &&
    filterValue.length > 0 &&
    filterValue[0].condition === "between";

  // Geo labels ("Within 50 mi of Boston, MA") read poorly when truncated, so
  // they get the same no-ellipsis treatment as ranges.
  const isGeoValue = filterValue !== undefined && isGeoFilter(filterValue);

  const isRange = isDateRange || isNumericRange || isGeoValue;

  const fontWeight = hasFilterValues ? 600 : 400;

  return (
    <button
      type="button"
      data-testid={`filter-tile-${filter}`}
      className={`flex w-full items-center justify-between rounded-lg px-2 py-[5px] h-[35px] border-0 text-left ${
        disabled
          ? "text-gray-500 cursor-not-allowed"
          : "text-[#2B4752] cursor-pointer"
      }`}
      style={{
        border: `1px solid ${getBorderColor()}`,
        background: getBackgroundColor(),
        fontWeight: fontWeight,
        boxShadow: getBoxShadow(),
      }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <div
        className={`flex gap-2 items-center flex-1 min-w-0 ${
          disabled ? "text-gray-500" : "text-[#2B4752]"
        }`}
      >
        {dataType ? getIconForDataType(dataType) : null}
        <div
          className="flex-1 min-w-0"
          style={{
            fontWeight: fontWeight,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {columnsTitleCreator
            ? columnsTitleCreator(filter)
            : toTitleCase(filter)}
        </div>

        {hasFilterValues && filterValue && !showAsAvailable && (
          <Tooltip title={displayText} mouseEnterDelay={1}>
            <span
              className="text-xs bg-blue-100 text-[#0D1685] px-2 py-1 rounded-full font-medium  shrink-0"
              style={{
                whiteSpace: "nowrap",
                overflow: isRange ? "visible" : "hidden",
                textOverflow: isRange ? "unset" : "ellipsis",
                maxWidth: isRange ? "none" : "120px",
              }}
            >
              {displayText}
            </span>
          </Tooltip>
        )}
      </div>
      <RightOutlined
        className="shrink-0 ml-1"
        style={{
          color: disabled
            ? "#9CA3AF"
            : `${isSelected || hasFilterValues ? "#2B4752" : "#556C75"}`,
          fontSize: "12px",
        }}
      />
    </button>
  );
};

export default FilterTile;
